import React, { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import Auth from './components/Auth';
import Dashboard from './components/Dashboard';
import UpdateView from './components/views/UpdateView';
import BetaView from './components/views/BetaView';
import PublicFirmwareShell from './components/views/PublicFirmwareShell';
import DownloadView from './components/views/DownloadView';
import LegacyDownloadView from './components/views/LegacyDownloadView';
import ReleasesHistory from './components/ReleasesHistory';
import MobileAppManagement from './components/MobileAppManagement';
import Footer from './components/Footer';
import BetaPortalView from './components/views/BetaPortalView';
import TestingPortalView from './components/views/TestingPortalView';
import StoreDashboardPortalView from './components/views/StoreDashboardPortalView';
import PublicDeployTestPage from './components/views/PublicDeployTestPage';
import { User } from './types';
import { API_BASE_URL } from './config/api';
import { hostRedirectUrl, isAdminHost, isBetaHost, isDashboardHost, isPublicPath, isTestingHost } from './config/hosts';

// Maps the current pathname to a human-friendly page name for the document title.
const getPageName = (pathname: string): string | null => {
  const path = pathname.replace(/\/+$/, '') || '/';

  if (path === '/' || path === '/devices/dashboard' || path.startsWith('/dashboard')) return 'Dashboard';
  if (path === '/devices/my-devices') return 'My Devices';
  if (path === '/devices') return 'Devices';
  if (path.startsWith('/users/')) return 'User Details';
  if (path === '/users') return 'Users';
  if (path === '/profile') return 'Profile';
  if (path === '/firmware') return 'Firmware';
  if (path === '/flash') return 'Flash';
  if (path === '/settings') return 'Settings';
  if (path === '/mobile-app') return 'Mobile App';
  if (path === '/surveys/new') return 'New Survey';
  if (path.includes('/results/questions/') && path.startsWith('/surveys/')) return 'Survey Question Responses';
  if (path.includes('/results/') && path.startsWith('/surveys/')) return 'Survey Result';
  if (path.endsWith('/results') && path.startsWith('/surveys/')) return 'Survey Results';
  if (path.startsWith('/surveys/')) return 'Edit Survey';
  if (path === '/surveys') return 'Surveys';
  if (path === '/deploy/internal/tests/new') return 'New Test';
  if (path.includes('/results/') && path.startsWith('/deploy/internal/tests/')) return 'Test Result';
  if (path.endsWith('/results') && path.startsWith('/deploy/internal/tests/')) return 'Test Results';
  if (path.startsWith('/deploy/internal/tests/')) return 'Edit Test';
  if (path === '/deploy/internal' || path.startsWith('/deploy/internal/')) return 'Internal Deploy';
  if (path === '/deploy/beta/tests/new') return 'New Test';
  if (path.includes('/results/') && path.startsWith('/deploy/beta/tests/')) return 'Test Result';
  if (path.endsWith('/results') && path.startsWith('/deploy/beta/tests/')) return 'Test Results';
  if (path.startsWith('/deploy/beta/tests/')) return 'Edit Test';
  if (path === '/deploy/beta' || path.startsWith('/deploy/beta/')) return 'Beta Deploy';
  if (path === '/deploy/production') return 'Production Deploy';
  if (path === '/deploy') return 'Deploy';
  if (path === '/store-dashboard') return 'Store Dashboard';
  if (path === '/logs/device') return 'Device Logs';
  if (path === '/logs/server') return 'Server Logs';
  if (path === '/changelog') return 'Server Changelog';
  if (path === '/update') return 'Update';
  if (path === '/beta') return 'Beta Update';
  if (path === '/download') return 'Download';
  if (path === '/legacy') return 'Legacy Download';
  if (path === '/releases') return 'Releases';
  if (path.startsWith('/take/')) return 'Beta Test';
  if (path.startsWith('/tests/')) return 'Beta Test';

  return null;
};

const App = () => {
  const [user, setUser] = useState<User | null>(null);
  const location = useLocation();

  const checkUser = async () => {
    const response = await fetch(`${API_BASE_URL}/auth/current-user`, { credentials: 'include' });
    if (response.ok) {
      const data = await response.json();
      setUser(data.user);
    }
  };

  useEffect(() => {
    const metaTag = document.createElement('meta');
    metaTag.name = "pixelair-web-flasher-version";
    metaTag.content = process.env.REACT_APP_VERSION || 'unknown';
    document.head.appendChild(metaTag);

    if (isBetaHost() || isTestingHost() || isDashboardHost() || !isPublicPath(window.location.pathname)) {
      checkUser();
    }
  }, []);

  useEffect(() => {
    if (isDashboardHost()) {
      if (!user) {
        document.title = 'Fluora Dashboard - Log in';
        return;
      }
      const path = location.pathname.replace(/\/+$/, '') || '/';
      document.title = path === '/apis'
        ? 'Fluora Dashboard - APIs'
        : path === '/sales'
          ? 'Fluora Dashboard - Sales'
          : path === '/fleet'
            ? 'Fluora Dashboard - Fleet'
            : path === '/support'
              ? 'Fluora Dashboard - Support'
              : path === '/work'
                ? 'Fluora Dashboard - Work'
                : path === '/sentry'
                  ? 'Fluora Dashboard - Sentry'
                  : path === '/surveys'
                    ? 'Fluora Dashboard - Surveys'
                    : path === '/marketing'
                      ? 'Fluora Dashboard - Marketing'
                      : path === '/social'
                        ? 'Fluora Dashboard - Social'
                        : 'Fluora Dashboard - Overview';
      return;
    }
    if (isBetaHost() || isTestingHost()) {
      const brand = isTestingHost() ? 'Internal Testing' : 'Fluora Beta';
      const path = location.pathname.replace(/\/+$/, '') || '/';
      if (path.startsWith('/tests/')) {
        document.title = `${brand} - Deploy Test`;
      } else if (user) {
        document.title = `${brand} - Your Devices`;
      } else if (path === '/login') {
        document.title = `${brand} - Log in`;
      } else if (path === '/register') {
        document.title = `${brand} - Create account`;
      } else {
        document.title = brand;
      }
      return;
    }
    const pageName = getPageName(location.pathname);
    const baseTitle = isAdminHost() ? 'Fluora Server' : 'Fluora Firmware';
    if (isAdminHost() && !user && !isPublicPath(location.pathname)) {
      document.title = 'Fluora Server - Login';
      return;
    }
    document.title = pageName ? `${baseTitle} - ${pageName}` : baseTitle;
  }, [location.pathname, user]);

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE_URL}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (_err) {
      // Still clear local state if the session is already gone.
    }
    setUser(null);
  };

  const hostRedirect = hostRedirectUrl(
    window.location.hostname,
    location.pathname,
    location.search,
    location.hash,
  );
  if (hostRedirect) {
    window.location.replace(hostRedirect);
    return null;
  }

  if (isDashboardHost()) {
    return (
      <StoreDashboardPortalView
        user={user}
        onAuth={(authenticatedUser) => setUser(authenticatedUser)}
        onLogout={handleLogout}
      />
    );
  }

  if (isBetaHost() || isTestingHost()) {
    const PortalView = isTestingHost() ? TestingPortalView : BetaPortalView;
    return (
      <Routes>
        <Route path="/tests/:name" element={<PublicDeployTestPage />} />
        <Route
          path="/*"
          element={(
            <PortalView
              user={user}
              onAuth={(authenticatedUser) => setUser(authenticatedUser)}
              onLogout={handleLogout}
            />
          )}
        />
      </Routes>
    );
  }

  const publicFirmwareLayout = (content: React.ReactNode) => (
    <PublicFirmwareShell>{content}</PublicFirmwareShell>
  );

  const authenticatedLayout = (content: React.ReactNode, showFooter = true) => (
    <div className="d-flex flex-column min-vh-100">
      <div className="flex-grow-1">{content}</div>
      {showFooter && <Footer />}
    </div>
  );

  return (
    <Routes>
      <Route path="/update" element={publicFirmwareLayout(<UpdateView />)} />
      <Route path="/beta" element={publicFirmwareLayout(<BetaView />)} />
      <Route path="/download" element={publicFirmwareLayout(<DownloadView />)} />
      <Route path="/legacy" element={publicFirmwareLayout(<LegacyDownloadView />)} />
      <Route path="/releases" element={publicFirmwareLayout(<ReleasesHistory />)} />
      <Route path="/mobile-app" element={publicFirmwareLayout(<MobileAppManagement publicView />)} />
      <Route path="/tests/:name" element={<PublicDeployTestPage />} />
      <Route
        path="/*"
        element={
          user?.role === 'admin' ? (
            <div className="admin-app-shell">
              <Dashboard user={user!} onLogout={handleLogout} />
            </div>
          ) : (
            authenticatedLayout(
              user ? (
                <Dashboard user={user} onLogout={handleLogout} />
              ) : (
                <Auth type="login" onAuth={(authenticatedUser) => setUser(authenticatedUser)} />
              ),
              true
            )
          )
        }
      />
    </Routes>
  );
};

export default App;
